import { Controller } from "react-hook-form";
import { TextInput, TextArea, Checkbox, Select, PhoneInput, DateField } from "../../ui";
// Bridge target for field types not yet rebuilt as _components/ui inputs
// (multiSelect, simplifyUser/company/market entity selectors, imageUpload,
// arrayText, listManager, tieredPricing, walletCreditsDynamic, richEditor/
// textEditor, segmentedToggle, radioButton, permissionSelector/custom,
// dateAndTime). Deliberate, still-shared old-tree boundary - see
// docs/external-dependency-audit.md. Shrinks as each type gets a native
// _components/ui equivalent in a later pass.
import renderInput from "../renderInput";
import {
  evaluateFieldVisibility,
  evaluateDisabledCondition,
  evaluateRequiredCondition,
} from "../schema/evaluateCondition";

const NUMBER_GUARD = {
  number: /^-?\d*$/,
  numberWithDecimal: /^-?\d*(\.\d*)?$/,
};

const NATIVE_SIMPLE_TYPES = new Set([
  "text",
  "email",
  "url",
  "number",
  "numberWithDecimal",
  "textArea",
  "checkbox",
  "checkboxContainer",
  "phoneNumber",
  "date",
]);

// singleSelect only has a native equivalent when the descriptor doesn't
// need the reusables select's isObject/dynamicConfig/custom-renderItem
// support - _components/ui/Select is a plain string|{label,value} list
// (see its own docstring). Most org-configured singleSelect fields resolve
// through useResolveFormSchema with isObject:true (industries, users,
// markets, ...), so those stay bridged; a plain static-options select goes
// native.
function isNativeCapableSelect(field) {
  return !field.isObject && !field.dynamicConfig && !field.renderItem;
}

function isNativeField(field) {
  if (NATIVE_SIMPLE_TYPES.has(field.type)) return true;
  if (field.type === "singleSelect") return isNativeCapableSelect(field);
  return false;
}

const bridgeWarned = new Set();
function warnBridgedOnce(type) {
  if (process.env.NODE_ENV === "production") return;
  if (bridgeWarned.has(type)) return;
  bridgeWarned.add(type);
  // eslint-disable-next-line no-console
  console.warn(
    `[DynamicForm] field type "${type}" has no native _components/ui input yet - ` +
      `rendering it through the legacy _components/form/renderInput bridge. ` +
      `See docs/external-dependency-audit.md.`
  );
}

/**
 * Renders one field descriptor from a dynamic-form schema. Native types
 * (see NATIVE_SIMPLE_TYPES) go through an RHF Controller straight into a
 * _components/ui input. Everything else bridges through the old renderInput
 * - it manages its own value/onChange/error/show/disabled entirely via the
 * `formData`/`handleChange`/`handleBlur` it's handed (both already backed
 * by the same react-hook-form instance via useDynamicForm), so no Controller
 * wrapper is needed for that path.
 */
const FormField = ({
  field,
  control,
  formData,
  errors,
  handleChange,
  handleBlur,
  allFields,
  mode,
  showToast,
  setImageFile,
  setShouldDeleteImage,
  context,
  filters,
  disabled = false,
}) => {
  if (field.type === "button") return null;
  if (!evaluateFieldVisibility(field, formData, allFields)) return null;

  if (!isNativeField(field)) {
    warnBridgedOnce(field.type);
    return renderInput(
      field,
      formData,
      errors,
      handleChange,
      handleBlur,
      setImageFile,
      setShouldDeleteImage,
      undefined, // pricingTiers - no bridged field in CompanyFormModal's schema needs tieredPricing yet
      undefined, // setPricingTiers
      allFields,
      mode,
      showToast,
      context,
      filters,
      disabled
    );
  }

  const isDisabled = disabled || evaluateDisabledCondition(field, formData, allFields, mode);

  return (
    <Controller
      name={field.name}
      control={control}
      render={({ field: rhfField, fieldState }) => {
        const commonProps = {
          id: field.name,
          label: field.label,
          error: field.skipBlurValidation ? undefined : fieldState.error?.message,
          disabled: isDisabled,
          required: evaluateRequiredCondition(field, formData, allFields),
          placeholder: field.placeholder,
          tooltip: field.tooltip,
          onBlur: rhfField.onBlur,
        };

        switch (field.type) {
          case "text":
          case "email":
          case "url":
            return (
              <TextInput
                {...commonProps}
                type={field.type}
                value={rhfField.value ?? ""}
                onChange={rhfField.onChange}
              />
            );

          case "number":
          case "numberWithDecimal":
            return (
              <TextInput
                {...commonProps}
                type="text"
                inputMode={field.type === "number" ? "numeric" : "decimal"}
                value={rhfField.value ?? ""}
                onChange={(value) => {
                  if (field.type === "numberWithDecimal" && value === ".") {
                    rhfField.onChange("0.");
                    return;
                  }
                  // Invalid keystrokes are silently dropped rather than
                  // toasting per-character like the old renderInput did -
                  // same end result (invalid input never lands), less noise.
                  if (NUMBER_GUARD[field.type].test(value)) {
                    rhfField.onChange(value);
                  }
                }}
              />
            );

          case "textArea":
            return (
              <TextArea
                {...commonProps}
                rows={field.rows}
                value={rhfField.value ?? ""}
                onChange={rhfField.onChange}
              />
            );

          case "checkbox":
          case "checkboxContainer":
            return (
              <Checkbox
                {...commonProps}
                label={field.title || field.label}
                description={field.description}
                value={Boolean(rhfField.value)}
                onChange={rhfField.onChange}
              />
            );

          case "singleSelect":
            return (
              <Select
                {...commonProps}
                options={(field.items || []).map((item) =>
                  typeof item === "object" && item !== null
                    ? item
                    : { label: item, value: item }
                )}
                value={rhfField.value}
                // Not rhfField.onChange directly: Select's own option button
                // lives in a portaled dropdown outside the trigger element,
                // so clicking an option blurs the trigger (native DOM focus
                // change) BEFORE React dispatches that button's onClick.
                // rhfField.onBlur fires first and kicks off an async
                // whole-form validation pass that still reads the OLD
                // (pre-selection) value; rhfField.onChange then updates the
                // value, but under mode:"onBlur" that alone doesn't queue a
                // revalidation, since this field had no recorded error yet
                // at that point. The stale blur-triggered pass lands a
                // render later, stamping a "required" error over an already
                // -filled field with nothing left to clear it. Forcing a
                // revalidation of just this field synchronously after the
                // value updates queues a second pass strictly after the
                // stale one, so its (correct, error-free) result is what
                // actually lands.
                onChange={(value) => handleChange(field.name, value, { forceRevalidate: true })}
                searchable={field.searchable}
              />
            );

          case "phoneNumber":
            return (
              <PhoneInput
                {...commonProps}
                label={field.label || "Phone number"}
                value={rhfField.value}
                onChange={rhfField.onChange}
              />
            );

          case "date":
            return (
              <DateField
                {...commonProps}
                value={rhfField.value}
                // Same portaled-picker blur-before-select race as
                // singleSelect above - see its comment.
                onChange={(value) => handleChange(field.name, value, { forceRevalidate: true })}
                noPastDates={field.noPastDates}
                noFutureDates={field.noFutureDates}
              />
            );

          default:
            return null;
        }
      }}
    />
  );
};

export default FormField;
